import {
  AccountInfo,
  BrokerConnector,
  Order,
  GTTOrder,
  Position,
  TickerItem
} from './types'
import { KiteConnect } from 'kiteconnect-ts'
import {
  getSecureItem,
  setSecureItem,
  removeSecureItem
} from './utils/secureStorage'

/**
 * Zerodha trading connector implementation
 * Provides integration with Zerodha's Kite Connect API for trading Indian stocks and derivatives
 * 
 * This connector enables trading on NSE, BSE and other Indian exchanges through Zerodha's platform.
 * It supports various order types, market data retrieval, and account management features.
 * 
 * @example
 * // Initialize the connector with API credentials
 * const zerodhaConnector = new ZerodhaConnector('your-api-key', 'your-api-secret');
 * 
 * // Connect to Zerodha
 * await zerodhaConnector.connect();
 * 
 * // Place a market order
 * const order = await zerodhaConnector.placeOrder({
 *   symbol: 'RELIANCE',
 *   quantity: 1,
 *   type: 'MARKET',
 *   side: 'BUY'
 * });
 */
export class ZerodhaConnector implements BrokerConnector {
  /**
   * Get GTT (Good Till Triggered) orders for a specific symbol
   * GTT orders are conditional orders that remain active until a trigger condition is met
   * 
   * @param {string} [symbol] The stock symbol to get GTT orders for (optional, returns all GTT orders if omitted)
   * @returns {Promise<GTTOrder[]>} Promise with array of GTT orders
   * 
   * @example
   * // Get all GTT orders
   * const allGTTOrders = await zerodhaConnector.getGTTOrders();
   * 
   * // Get GTT orders for a specific stock
   * const relianceGTTOrders = await zerodhaConnector.getGTTOrders('RELIANCE');
   */
  async getGTTOrders(symbol?: string) {
    try {
      if (!this.accessToken) {
        throw new Error('Access token is required');
      }
      
      // Use the API endpoint to fetch real GTT orders
      const response = await fetch('/api/connectors/zerodha/gtt/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbol: symbol || 'ALL'
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to fetch GTT orders');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error fetching GTT orders:', error);
      throw new Error('Failed to fetch GTT orders');
    }
  }
  
  private kite: any
  private apiKey: string
  private apiSecret: string
  private accessToken?: string
  private isConnected: boolean = false

  /**
   * Create a new Zerodha connector instance
   * 
   * @param {string} apiKey The API key obtained from Zerodha developer console
   * @param {string} apiSecret The API secret obtained from Zerodha developer console
   * 
   * @example
   * const connector = new ZerodhaConnector('your-api-key', 'your-api-secret');
   */
  constructor(apiKey: string, apiSecret: string) {
    this.apiKey = apiKey
    this.apiSecret = apiSecret
    this.kite = new KiteConnect({ api_key: apiKey })
    
    // Auto-connect if we have valid credentials
    if (typeof window !== 'undefined' && this.apiKey && this.apiSecret) {
      // Set isConnected to true if we have credentials from session storage
      const storedApiKey = getSecureItem('zerodha_api_key')
      const storedApiSecret = getSecureItem('zerodha_api_secret')
      const storedAccessToken = getSecureItem('zerodha_access_token')

      if (storedApiKey === this.apiKey && storedApiSecret === this.apiSecret && storedAccessToken) {
        this.accessToken = storedAccessToken
        this.kite.setAccessToken(this.accessToken)
        this.isConnected = true
        console.log('ZerodhaConnector initialized from stored credentials')
      }
    }
  }

  /**
   * Check if the connector is currently connected to Zerodha
   * Also validates if the access token is still valid and initiates re-authentication if needed
   * 
   * @returns {boolean} True if connected with a valid token, false otherwise
   * 
   * @example
   * if (zerodhaConnector.isConnectedToBroker()) {
   *   // Proceed with trading operations
   * } else {
   *   // Show login UI or trigger reconnection
   * }
   */
  isConnectedToBroker(): boolean {
    if (!this.isConnected || !this.accessToken) return false;
    
    // Check token validity and trigger re-authentication if needed
    this.isAccessTokenValid().then(isValid => {
      if (!isValid) {
        console.log('Token expired or invalid, initiating re-authentication...');
        this.isConnected = false;
        this.accessToken = undefined;
        // Clear stored credentials
        if (typeof window !== 'undefined') {
          removeSecureItem('zerodha_access_token');
        }
        // Trigger re-authentication popup
        this.authenticateWithPopup().catch(error => {
          console.error('Re-authentication failed:', error);
        });
      }
    }).catch(error => {
      console.error('Token validation check failed:', error);
      this.isConnected = false;
      return false;
    });
    
    return this.isConnected;
  }

  /**
   * Check if the current access token is valid and not expired
   * Makes a test API call to verify token validity
   * 
   * @returns {Promise<boolean>} Promise resolving to true if token is valid, false otherwise
   * @private
   */
  private async isAccessTokenValid(): Promise<boolean> {
    if (!this.accessToken) return false;
    
    try {
      // Make a test API call to check token validity
      const response = await fetch('/api/connectors/zerodha/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.error_type === 'TokenException') {
          console.log('Access token has expired');
          return false;
        }
        throw new Error(errorData.message || `API error: ${response.status}`);
      }

      return true;
    } catch (error) {
      console.error('Error checking token validity:', error);
      return false;
    }
  }

  /**
   * Authenticate with Zerodha using a popup window
   * Opens the Zerodha login page in a popup and waits for authentication to complete
   * 
   * @returns {Promise<boolean>} Promise resolving to true if authentication was successful
   * @throws {Error} If popup is blocked or authentication fails
   * 
   * @example
   * try {
   *   const success = await zerodhaConnector.authenticateWithPopup();
   *   if (success) {
   *     console.log('Successfully authenticated with Zerodha');
   *   }
   * } catch (error) {
   *   console.error('Authentication failed:', error);
   * }
   */
  async authenticateWithPopup(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      try {
        // Validate API key and secret
        if (!this.apiKey || !this.apiSecret) {
          reject(new Error('API Key and API Secret are required'));
          return;
        }

        // Flag to track if we've received a token
        let tokenReceived = false;
        
        // Setup message listener to receive the request token from the popup
        const messageHandler = (event: MessageEvent) => {
          // Check if the message contains a request token
          if (event.data?.requestToken) {
            console.log('Received request token:', event.data.requestToken);
            
            // Set flag to indicate we've received the token
            tokenReceived = true;
            
            // Remove the event listener once we've received the token
            window.removeEventListener('message', messageHandler);
            
            // Process the request token
            this.processRequestToken(event.data.requestToken)
              .then(() => {
                console.log('Successfully processed request token');
                resolve(true);
              })
              .catch((error) => {
                console.error('Failed to process request token:', error);
                reject(error);
              });
          }
        };
        
        // Add event listener for messages from the popup
        window.addEventListener('message', messageHandler);
        
        // Open the Zerodha login popup with the correct URL
        const loginUrl = `https://kite.zerodha.com/connect/login?v=3&api_key=${this.apiKey}`;
        console.log('Opening Zerodha login popup with URL:', loginUrl);
        
        // Calculate popup dimensions for better UX
        const width = 800;
        const height = 600;
        const left = window.screenX + (window.outerWidth - width) / 2;
        const top = window.screenY + (window.outerHeight - height) / 2;
        
        const popup = window.open(
          loginUrl, 
          'loginPopup', 
          `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,status=yes`
        );
        
        if (!popup) {
          window.removeEventListener('message', messageHandler);
          reject(new Error('Popup was blocked. Please allow popups for this site.'));
          return;
        }
        
        // Add a longer delay before starting to check if popup is closed
        // This gives user time to complete the authentication process
        setTimeout(() => {
          // Handle popup closed without authentication
          const checkPopupClosed = setInterval(() => {
            // Only consider it cancelled if popup is closed AND we haven't received a token
            if (popup && popup.closed && !tokenReceived) {
              clearInterval(checkPopupClosed);
              window.removeEventListener('message', messageHandler);
              reject(new Error('Authentication was cancelled'));
            } else if (tokenReceived) {
              // If token was received, we don't need to check anymore
              clearInterval(checkPopupClosed);
            }
          }, 2000); // Check less frequently (every 2 seconds)
        }, 5000); // Wait 5 seconds before starting to check
        
      } catch (error) {
        console.error('Zerodha popup authentication failed:', error);
        reject(error);
      }
    });
  }
  
  // Process the request token and generate a session
  // Made public so it can be called directly when needed
  async processRequestToken(requestToken: string): Promise<boolean> {
    try {
      if (!requestToken) {
        console.error('Request token is empty or undefined');
        throw new Error('Request token is required');
      }

      console.log('Processing request token:', requestToken);
      
      // Generate session using the request token
      const sessionResponse = await this.generateSession(requestToken);
      
      if (!sessionResponse || !sessionResponse.access_token) {
        console.error('Invalid session response:', sessionResponse);
        throw new Error('Failed to get access token from session response');
      }
      
      console.log('Session generated successfully with access token');
      
      // Store credentials in session storage
      if (typeof window !== 'undefined') {
        setSecureItem('zerodha_api_key', this.apiKey);
        setSecureItem('zerodha_api_secret', this.apiSecret);
        setSecureItem('zerodha_access_token', sessionResponse.access_token);
      }
      
      // Set the access token and mark as connected
      this.accessToken = sessionResponse.access_token;
      this.kite.setAccessToken(this.accessToken);
      this.isConnected = true;
      return true;
    } catch (error) {
      // Provide more detailed error information
      if (error instanceof Error) {
        console.error('Failed to generate session:', error.message);
      } else {
        console.error('Failed to generate session:', JSON.stringify(error));
      }
      this.isConnected = false;
      return false;
    }
  }

  // Helper method to generate session using request token
  private async generateSession(requestToken: string): Promise<any> {
    try {
      if (!requestToken) {
        throw new Error('Request token is required for session generation');
      }
      
      if (!this.apiKey || !this.apiSecret) {
        throw new Error('API key and secret are required for session generation');
      }
      
      console.log('Generating session with request token:', requestToken);
      
      // Use our server-side API endpoint to avoid CORS issues
      const response = await fetch('/api/connectors/zerodha/session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          requestToken: requestToken
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Session API error:', response.status, errorData);
        throw new Error(errorData.error || `Session API error: ${response.status}`);
      }
      
      const sessionData = await response.json();
      console.log('Session generation response:', sessionData);
      
      // Handle the response from our server endpoint which returns the raw Zerodha API response
      if (!sessionData || !sessionData.data || !sessionData.data.access_token) {
        throw new Error('Invalid response from Kite API: ' + JSON.stringify(sessionData));
      }
      
      // Set the access token in the kite instance
      this.kite.setAccessToken(sessionData.data.access_token);
      
      return sessionData.data;
    } catch (error) {
      console.error('Error generating session:', error);
      // Provide more detailed error information
      if (error instanceof Error) {
        throw new Error(`Session generation failed: ${error.message}`);
      } else {
        throw new Error(`Session generation failed: ${JSON.stringify(error)}`);
      }
    }
  }

  // Helper methods for authentication have been simplified

  /**
   * Connect to the Zerodha trading platform
   * Establishes a connection to Zerodha by authenticating with API credentials
   * If already connected, returns true without re-authenticating
   * 
   * @returns {Promise<boolean>} Promise resolving to true if connection is successful, false otherwise
   * @throws {Error} If API credentials are invalid or authentication fails
   * 
   * @example
   * try {
   *   const connected = await zerodhaConnector.connect();
   *   if (connected) {
   *     console.log('Connected to Zerodha');
   *   }
   * } catch (error) {
   *   console.error('Connection failed:', error);
   * }
   */
  async connect(): Promise<boolean> {
    try {
      // Validate that the API key and secret are provided
      if (!this.apiKey || !this.apiSecret) {
        throw new Error('API key and secret are required')
      }

      // Check if we're already connected with valid credentials
      if (this.isConnectedToBroker()) {
        console.log('Already connected to Zerodha')
        return true
      }

      // Use popup authentication if we're in a browser environment
      if (typeof window !== 'undefined') {
        console.log('Attempting popup authentication flow...')
        try {
          const popupAuthSuccess = await this.authenticateWithPopup()
          if (popupAuthSuccess) {
            console.log('Successfully connected to Zerodha via popup authentication')
            return true
          }
        } catch (popupError) {
          console.error('Popup authentication failed:', popupError)
          throw new Error('Authentication failed')
        }
      } else {
        throw new Error('Cannot authenticate outside of browser environment')
      }

      return false
    } catch (error) {
      console.error('Zerodha connection failed:', error)
      this.isConnected = false
      return false
    }
  }

  /**
   * Disconnect from the Zerodha trading platform
   * Clears stored credentials and resets the connection state
   * 
   * @returns {Promise<boolean>} Promise resolving to true if disconnection is successful
   * 
   * @example
   * await zerodhaConnector.disconnect();
   * console.log('Disconnected from Zerodha');
   */
  async disconnect(): Promise<boolean> {
    try {
      // Clear stored credentials
      if (typeof window !== 'undefined') {
        removeSecureItem('zerodha_api_key')
        removeSecureItem('zerodha_api_secret')
        removeSecureItem('zerodha_access_token')
      }

      this.accessToken = undefined
      this.isConnected = false
      return true
    } catch (error) {
      console.error('Zerodha disconnection failed:', error)
      return false
    }
  }

  /**
   * Place a trading order on Zerodha
   * Supports various order types including MARKET, LIMIT, SL (Stop Loss), and SL-M (Stop Loss Market)
   * 
   * @param {Partial<Order>} order The order details including symbol, quantity, price, etc.
   * @returns {Promise<Order>} Promise resolving to the placed order with order ID and status
   * @throws {Error} If not connected or order parameters are invalid
   * 
   * @example
   * // Place a market buy order
   * const marketOrder = await zerodhaConnector.placeOrder({
   *   symbol: 'RELIANCE',
   *   quantity: 1,
   *   type: 'MARKET',
   *   side: 'BUY',
   *   exchange: 'NSE'
   * });
   * 
   * @example
   * // Place a limit sell order
   * const limitOrder = await zerodhaConnector.placeOrder({
   *   symbol: 'INFY',
   *   quantity: 10,
   *   price: 1500,
   *   type: 'LIMIT',
   *   side: 'SELL',
   *   exchange: 'NSE'
   * });
   * 
   * @example
   * // Place a stop loss order
   * const stopLossOrder = await zerodhaConnector.placeOrder({
   *   symbol: 'TATASTEEL',
   *   quantity: 5,
   *   price: 140,
   *   type: 'SL',
   *   side: 'SELL',
   *   triggerPrice: 145,
   *   exchange: 'NSE'
   * });
   */
  async placeOrder(order: Partial<Order>): Promise<Order> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      const { symbol, quantity, price, type, side, triggerPrice, exchange } = order

      if (!symbol || !quantity || !side) {
        throw new Error('Symbol, quantity, and side are required')
      }

      // Validate order parameters based on order type
      if (type === 'SL' || type === 'SL-M') {
        if (!triggerPrice) {
          throw new Error('Trigger price is required for SL and SL-M orders')
        }
        
        // For SL orders, price is also required
        if (type === 'SL' && !price) {
          throw new Error('Price is required for SL orders')
        }
      }

      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          order: {
            symbol,
            quantity,
            price,
            type,
            side,
            triggerPrice,
            exchange: exchange || 'NSE' // Default to NSE if not provided
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to place order')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to place order:', error)
      throw error
    }
  }
  
  /**
   * Place a Good Till Triggered (GTT) order on Zerodha
   * GTT orders remain active until the specified trigger condition is met
   * Supports both SINGLE and OCO (One Cancels Other) trigger types
   * 
   * @param {Partial<GTTOrder>} order The GTT order details
   * @returns {Promise<Order>} Promise resolving to the placed GTT order
   * @throws {Error} If not connected or order parameters are invalid
   * 
   * @example
   * // Place a GTT buy order that triggers when price falls to a certain level
   * const gttBuyOrder = await zerodhaConnector.placeGTT({
   *   symbol: 'HDFCBANK',
   *   quantity: 5,
   *   price: 1650,  // Limit price to buy at
   *   side: 'BUY',
   *   triggerPrice: 1700,  // Trigger when price falls to 1700
   *   triggerType: 'SINGLE',
   *   product: 'CNC',
   *   exchange: 'NSE'
   * });
   * 
   * @example
   * // Place an OCO (One Cancels Other) GTT order with target and stop loss
   * const ocoOrder = await zerodhaConnector.placeGTT({
   *   symbol: 'TATAMOTORS',
   *   quantity: 10,
   *   price: 450,  // Current price
   *   side: 'SELL',
   *   triggerPrice: 500,  // Target price
   *   triggerType: 'OCO',
   *   product: 'CNC',
   *   exchange: 'NSE',
   *   lastPrice: 450  // Used to calculate the stop loss for OCO
   * });
   */
  async placeGTT(order: Partial<GTTOrder>): Promise<Order> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      const { symbol, quantity, price, side, triggerPrice, triggerType, product, exchange, lastPrice } = order

      if (!symbol || !quantity || !side || !triggerPrice) {
        throw new Error('Symbol, quantity, side, and trigger price are required for GTT orders')
      }
      
      // Default values
      const orderProduct = product || 'CNC' // Default to CNC (delivery)
      const orderTriggerType = triggerType || 'SINGLE' // Default to SINGLE
      
      // Extract exchange from symbol if not provided
      let orderExchange = exchange
      if (!orderExchange && typeof symbol === 'string') {
        const match = symbol.match(/\.(NS|BS)$/i)
        if (match) {
          orderExchange = match[1].toUpperCase() === 'NS' ? 'NSE' : 'BSE'
        }
      }
      
      if (!orderExchange) {
        throw new Error('Exchange is required for GTT orders')
      }
      
      // Use our server-side API endpoint for GTT orders
      const response = await fetch('/api/connectors/zerodha/gtt', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          order: {
            symbol,
            quantity,
            price,
            side,
            triggerPrice,
            triggerType: orderTriggerType,
            product: orderProduct,
            exchange: orderExchange,
            lastPrice
          }
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to place GTT order')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to place GTT order:', error)
      throw error
    }
  }

  /**
   * Get all active orders from Zerodha
   * Retrieves all orders that have been placed but not yet completed or cancelled
   * 
   * @returns {Promise<Order[]>} Promise resolving to an array of orders
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const activeOrders = await zerodhaConnector.getOrders();
   * console.log(`You have ${activeOrders.length} active orders`);
   */
  async getOrders(): Promise<Order[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch orders')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch orders:', error)
      return []
    }
  }

  /**
   * Get all current positions from Zerodha
   * Retrieves all day and carry-forward positions with current profit/loss values
   * 
   * @returns {Promise<Position[]>} Promise resolving to an array of positions
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const positions = await zerodhaConnector.getPositions();
   * 
   * // Calculate total P&L
   * const totalPnL = positions.reduce((sum, position) => sum + position.pnl, 0);
   * console.log(`Total P&L: ${totalPnL}`);
   */
  async getPositions(): Promise<Position[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/positions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch positions')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch positions:', error)
      return []
    }
  }

  /**
   * Get account balance from Zerodha
   * Returns the available cash balance for trading
   * 
   * @returns {Promise<number>} Promise resolving to the account balance as a number
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const balance = await zerodhaConnector.getBalance();
   * console.log(`Available balance: ₹${balance.toFixed(2)}`);
   */
  async getBalance(): Promise<number> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/balance', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch balance')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch balance:', error)
      return 0
    }
  }

  /**
   * Get user's assets/holdings from Zerodha
   * Retrieves all stocks and securities owned by the user
   * 
   * @returns {Promise<any[]>} Promise resolving to an array of assets
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const holdings = await zerodhaConnector.getUserAssets();
   * 
   * // Print all holdings
   * holdings.forEach(holding => {
   *   console.log(`${holding.tradingsymbol}: ${holding.quantity} shares, current value: ₹${holding.last_price * holding.quantity}`);
   * });
   */
  async getUserAssets(): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/assets', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch user assets')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch user assets:', error)
      return []
    }
  }

  /**
   * Get order history from Zerodha
   * Retrieves historical orders with their execution details
   * 
   * @param {string} [symbol] Optional symbol to filter orders by
   * @returns {Promise<any[]>} Promise resolving to an array of historical orders
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get all order history
   * const allOrders = await zerodhaConnector.getOrderHistory();
   * 
   * // Get order history for a specific symbol
   * const relianceOrders = await zerodhaConnector.getOrderHistory('RELIANCE');
   */
  async getOrderHistory(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch order history')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch order history:', error)
      return []
    }
  }
  
  /**
   * Get trade history from Zerodha
   * Retrieves historical trades/executions
   * 
   * @param {string} [symbol] Optional symbol to filter trades by
   * @returns {Promise<any[]>} Promise resolving to an array of historical trades
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get all trade history
   * const allTrades = await zerodhaConnector.getTradeHistory();
   * 
   * // Get trade history for a specific symbol
   * const relianceTrades = await zerodhaConnector.getTradeHistory('RELIANCE');
   */
  async getTradeHistory(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/trades', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch trade history')
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch trade history:', error)
      return []
    }
  }

  /**
   * Get market data for specified symbols
   * Retrieves current price, change percentage, and volume data
   * 
   * @param {Object} [options] Optional parameters for the request
   * @param {string} [options.symbol] Single symbol to fetch data for
   * @param {string[]} [options.symbols] Array of symbols to fetch data for
   * @returns {Promise<TickerItem[]>} Promise resolving to an array of ticker items
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get data for a single symbol
   * const relianceData = await zerodhaConnector.getTickers({ symbol: 'RELIANCE' });
   * 
   * // Get data for multiple symbols
   * const data = await zerodhaConnector.getTickers({ symbols: ['RELIANCE', 'TCS', 'INFY'] });
   */
  async getTickers(options?: { symbol?: string; symbols?: string[] }): Promise<TickerItem[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use the server-side API endpoint to fetch ticker data
      const response = await fetch('/api/connectors/zerodha/tickers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          // Pass optional symbol or symbols parameters if provided
          ...(options?.symbol && { symbol: options.symbol }),
          ...(options?.symbols && { symbols: options.symbols })
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch ticker data')
      }

      const tickerData = await response.json()

      // Transform the data to match the expected format
      return tickerData.map((ticker: any) => ({
        symbol: ticker.symbol,
        originalSymbol: ticker.originalSymbol, // Add originalSymbol property
        exchange: ticker.exchange || 'NSE', // Add exchange property with default
        price: parseFloat(ticker.price),
        priceChangePercent: parseFloat(ticker.priceChangePercent),
        volume: parseFloat(ticker.volume)
      }))
    } catch (error) {
      console.error('Error fetching Zerodha tickers:', error)
      throw error
    }
  }

  /**
   * Get list of favorite symbols from Zerodha
   * Retrieves the user's watchlist/favorite symbols
   * 
   * @returns {Promise<string[]>} Promise resolving to an array of favorite symbol strings
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const favorites = await zerodhaConnector.getFavorites();
   * console.log('My favorite stocks:', favorites);
   */
  async getFavorites(): Promise<string[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // For now, return a static list of popular Indian stocks
      // In a real implementation, you would fetch this from user preferences
      return [
        'RELIANCE',
        'TCS',
        'HDFCBANK',
        'INFY',
        'ICICIBANK',
        'HINDUNILVR',
        'SBIN',
        'BAJFINANCE',
        'BHARTIARTL',
        'KOTAKBANK'
      ]
    } catch (error) {
      console.error('Error fetching Zerodha favorites:', error)
      return [] // Return empty array on error
    }
  }

  /**
   * Get open orders from Zerodha
   * Retrieves all orders that are currently open/pending
   * 
   * @param {string} [symbol] Optional symbol to filter orders by
   * @returns {Promise<any[]>} Promise resolving to an array of open orders
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get all open orders
   * const allOpenOrders = await zerodhaConnector.getOpenOrders();
   * 
   * // Get open orders for a specific symbol
   * const relianceOpenOrders = await zerodhaConnector.getOpenOrders('RELIANCE');
   */
  async getOpenOrders(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/openorders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch open orders')
      }

      const orders = await response.json()
      
      // Process orders to identify SL/TP types based on trigger price and order type
      return orders.map((order: any) => {
        // Add additional fields for order type identification
        if (order.triggerPrice > 0) {
          if (order.type === 'LIMIT') {
            order.orderType = 'SL-L' // Stop Loss Limit
          } else {
            order.orderType = 'SL-M' // Stop Loss Market
          }
        } else {
          order.orderType = order.type
        }
        
        return order
      })
    } catch (error) {
      console.error('Failed to fetch open orders:', error)
      return []
    }
  }

  /**
   * Get chart-formatted open orders from Zerodha
   * Retrieves open orders in a format suitable for chart display
   * 
   * @param {string} [symbol] Optional symbol to filter orders by
   * @returns {Promise<any[]>} Promise resolving to an array of chart-formatted orders
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get all chart orders
   * const chartOrders = await zerodhaConnector.getChartOpenOrders();
   * 
   * // Get chart orders for a specific symbol
   * const relianceChartOrders = await zerodhaConnector.getChartOpenOrders('RELIANCE');
   */
  async getChartOpenOrders(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Fetch both regular open orders and GTT orders
      const [regularOrders, gttOrders] = await Promise.all([
        // Fetch regular open orders
        (async () => {
          try {
            const response = await fetch('/api/connectors/zerodha/openorders', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                apiKey: this.apiKey,
                apiSecret: this.apiSecret,
                accessToken: this.accessToken,
                symbol: symbol
              })
            })

            if (!response.ok) {
              const errorData = await response.json()
              throw new Error(errorData.message || 'Failed to fetch open orders')
            }

            return await response.json()
          } catch (error) {
            console.error('Failed to fetch regular open orders:', error)
            return []
          }
        })(),
        
        // Fetch GTT orders
        (async () => {
          try {
            // Use the getGTTOrders method we already have
            const gttOrders = await this.getGTTOrders(symbol)
            return gttOrders || []
          } catch (error) {
            console.error('Failed to fetch GTT orders:', error)
            return []
          }
        })()
      ])

      console.log('Zerodha regular orders:', regularOrders.length)
      console.log('Zerodha GTT orders:', gttOrders.length)
      
      // Transform regular orders to chart format
      const formattedRegularOrders = regularOrders.map((order: any) => {
        // Format the time string
        const timeStr = order.createdAt 
          ? new Date(order.createdAt).toISOString().replace('T', ' ').substring(0, 19)
          : new Date().toISOString().replace('T', ' ').substring(0, 19)
        
        // Determine if this is a stop-loss order based on triggerPrice
        const hasStopLoss = order.triggerPrice && order.triggerPrice > 0
        
        return {
          time: timeStr,
          symbol: order.symbol.toLowerCase(),
          side: order.side,
          limitPrice: order.price,
          size: order.quantity,
          // Include stopLoss if triggerPrice exists
          stopLoss: hasStopLoss ? order.triggerPrice : undefined,
          // We don't have take profit in Zerodha orders
          takeProfit: undefined,
          orderId: order.orderId,
          // orderType: order.type
        }
      })
      
      // Transform GTT orders to chart format
      const formattedGTTOrders = gttOrders.map((order: any) => {
        // Format the time string - GTT orders might not have createdAt
        const timeStr = order.createdAt 
          ? new Date(order.createdAt).toISOString().replace('T', ' ').substring(0, 19)
          : new Date().toISOString().replace('T', ' ').substring(0, 19)
        console.log('GTT order:', order)
        console.log('GTT order symbol:', order.symbol)
        return {
          time: timeStr,
          symbol: order.symbol.toLowerCase(),
          side: order.side,
          // For GTT orders, the triggerPrice is the price level we want to show on the chart
          limitPrice: order.limitPrice,
          size: order.quantity,
          // We don't use stopLoss for GTT visualization
          stopLoss: undefined,
          // We don't use takeProfit for GTT visualization
          takeProfit: undefined,
          orderId: order.id,
          type: order.type
          // Add a flag to identify this as a GTT order
          // isGTT: true
        }
      })
      
      // Combine both types of orders
      return [...formattedRegularOrders, ...formattedGTTOrders]
    } catch (error) {
      console.error('Failed to fetch chart open orders:', error)
      return []
    }
  }

  /**
   * Get closed/completed orders from Zerodha
   * Retrieves orders that have been filled or cancelled
   * 
   * @param {string} [symbol] Optional symbol to filter orders by
   * @returns {Promise<any[]>} Promise resolving to an array of closed orders
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get all closed orders
   * const closedOrders = await zerodhaConnector.getClosedOrders();
   * 
   * // Get closed orders for a specific symbol
   * const relianceClosedOrders = await zerodhaConnector.getClosedOrders('RELIANCE');
   */
  async getClosedOrders(symbol?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint to get all orders
      const response = await fetch('/api/connectors/zerodha/orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbol: symbol
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch orders')
      }

      const orders = await response.json()
      
      // Filter for closed orders only
      return orders.filter((order: any) => 
        order.status === 'FILLED' || order.status === 'CANCELLED'
      )
    } catch (error) {
      console.error('Failed to fetch closed orders:', error)
      return []
    }
  }

  private webSocket: WebSocket | null = null
  private webSocketConnected: boolean = false
  private reconnectAttempts: number = 0
  private readonly MAX_RECONNECT_ATTEMPTS = 5
  private readonly RECONNECT_DELAY = 5000

  // getWebSocketUrl(): string {
  //   return `wss://ws.kite.trade?api_key=${this.apiKey}&access_token=${this.accessToken}`;
  // }
    // Get WebSocket URL for real-time data
  getWebSocketUrl(): string {
    return '/api/connectors/zerodha/stream';
  }

  /**
   * Get the current access token
   * Provides the token needed for API authentication
   * 
   * @returns {Promise<string>} Promise resolving to the access token
   * @throws {Error} If not connected or token not available
   * 
   * @example
   * const token = await zerodhaConnector.getAccessToken();
   * console.log('Current access token:', token);
   */
  async getAccessToken(): Promise<string> {
    if (!this.accessToken) {
      throw new Error('Not connected to Zerodha or access token not available');
    }
    return this.accessToken;
  }

  /**
   * Get the API key
   * Provides the API key used for authentication
   * 
   * @returns {Promise<string>} Promise resolving to the API key
   * 
   * @example
   * const apiKey = await zerodhaConnector.getApiKey();
   */
  async getApiKey(): Promise<string> {
    return this.apiKey;
  }

  /**
   * Get list of tradable instruments
   * Retrieves the list of instruments available for trading
   * 
   * @param {string} [exchange] Optional exchange to filter instruments by (NSE, BSE, NFO, etc.)
   * @returns {Promise<any[]>} Promise resolving to an array of instruments
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get all instruments
   * const allInstruments = await zerodhaConnector.getInstruments();
   * 
   * // Get only NSE instruments
   * const nseInstruments = await zerodhaConnector.getInstruments('NSE');
   */
  async getInstruments(exchange?: string): Promise<any[]> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha');
    }

    try {
      // Use our server-side API endpoint to fetch instruments
      const response = await fetch(`/api/connectors/zerodha/instruments${exchange ? `/${exchange}` : ''}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch instruments');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch instruments:', error);
      return [];
    }
  }

  /**
   * Get market quotes for specified symbols
   * Retrieves detailed market quotes including bid/ask prices, volumes, etc.
   * 
   * @param {string[]} symbols Array of symbols to get quotes for
   * @returns {Promise<any>} Promise resolving to quotes data
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const quotes = await zerodhaConnector.getQuotes(['RELIANCE', 'TCS', 'INFY']);
   * console.log('RELIANCE bid price:', quotes.RELIANCE.depth.buy[0].price);
   */
  async getQuotes(symbols: string[]): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha');
    }

    try {
      // Use our server-side API endpoint to fetch quotes
      const response = await fetch('/api/connectors/zerodha/quotes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbols: symbols
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch quotes');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
      return {};
    }
  }

  /**
   * Get OHLC quotes for specified symbols
   * Retrieves open, high, low, close data for the symbols
   * 
   * @param {string[]} symbols Array of symbols to get OHLC data for
   * @returns {Promise<any>} Promise resolving to OHLC data
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const ohlc = await zerodhaConnector.getOHLC(['RELIANCE', 'TCS', 'INFY']);
   * console.log('RELIANCE closing price:', ohlc.RELIANCE.close);
   */
  async getOHLC(symbols: string[]): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha');
    }

    try {
      // Use our server-side API endpoint to fetch OHLC data
      const response = await fetch('/api/connectors/zerodha/ohlc', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbols: symbols
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch OHLC data');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch OHLC data:', error);
      return {};
    }
  }

  /**
   * Get LTP (Last Traded Price) for specified symbols
   * Retrieves the last traded price for the symbols
   * 
   * @param {string[]} symbols Array of symbols to get LTP for
   * @returns {Promise<any>} Promise resolving to LTP data
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const ltp = await zerodhaConnector.getLTP(['RELIANCE', 'TCS', 'INFY']);
   * console.log('RELIANCE last price:', ltp.RELIANCE.last_price);
   */
  async getLTP(symbols: string[]): Promise<any> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha');
    }

    try {
      // Use our server-side API endpoint to fetch LTP data
      const response = await fetch('/api/connectors/zerodha/ltp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbols: symbols
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch LTP data');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to fetch LTP data:', error);
      return {};
    }
  }

  private setupWebSocket() {
    if (!this.isConnected || !this.accessToken) {
      throw new Error('Must be connected with valid access token to use WebSocket')
    }

    if (this.webSocket) {
      this.webSocket.close()
    }

    this.webSocket = new WebSocket(this.getWebSocketUrl())

    this.webSocket.onopen = () => {
      console.log('WebSocket connected')
      this.webSocketConnected = true
      this.reconnectAttempts = 0
    }

    this.webSocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data)
        // Handle different types of messages
        if (data.type === 'tick') {
          // Process market data tick
          console.log('Received tick:', data)
        } else if (data.type === 'order') {
          // Process order update
          console.log('Received order update:', data)
        }
      } catch (error) {
        console.error('Error processing WebSocket message:', error);
      }
    }

    this.webSocket.onerror = (error) => {
      console.error('WebSocket error:', error);
      this.webSocketConnected = false;
    }

    this.webSocket.onclose = () => {
      console.log('WebSocket closed');
      this.webSocketConnected = false;

      // Attempt reconnection if not manually disconnected
      if (this.isConnected && this.reconnectAttempts < this.MAX_RECONNECT_ATTEMPTS) {
        setTimeout(() => {
          console.log('Attempting to reconnect WebSocket...');
          this.reconnectAttempts++;
          this.setupWebSocket();
        }, this.RECONNECT_DELAY);
      }
    }
  }

  /**
   * Subscribe to real-time ticker data for symbols
   * Starts streaming real-time market data for the specified symbols
   * 
   * @param {string[]} symbols Array of symbols to subscribe to
   * @returns {Promise<void>}
   * @throws {Error} If WebSocket is not connected
   * 
   * @example
   * await zerodhaConnector.subscribeToTickers(['RELIANCE', 'TCS', 'INFY']);
   * // Now you'll receive real-time updates for these symbols
   */
  async subscribeToTickers(symbols: string[]) {
    if (!this.webSocket || !this.webSocketConnected) {
      await this.setupWebSocket();
    }

    if (this.webSocket && this.webSocketConnected) {
      // First subscribe to the instruments
      this.webSocket.send(JSON.stringify({
        type: 'subscribe',
        symbols: symbols
      }))
    } else {
      throw new Error('WebSocket is not connected');
    }
  }

  /**
   * Unsubscribe from real-time ticker data
   * Stops streaming real-time market data for the specified symbols
   * 
   * @param {string[]} symbols Array of symbols to unsubscribe from
   * @returns {Promise<void>}
   * 
   * @example
   * await zerodhaConnector.unsubscribeFromTickers(['RELIANCE', 'TCS', 'INFY']);
   * // No longer receiving updates for these symbols
   */
  async unsubscribeFromTickers(symbols: string[]) {
    if (this.webSocket && this.webSocketConnected) {
      this.webSocket.send(JSON.stringify({
        type: 'unsubscribe',
        symbols: symbols
      }))
    }
  }

    /**
   * Get options chain data for a specific symbol and expiry
   * Retrieves options chain with strike prices, premiums, and other data
   * 
   * @param {string} symbol The underlying symbol (e.g., 'NIFTY', 'BANKNIFTY')
   * @param {string} [expiry] Optional expiry date in YYYY-MM-DD format (uses nearest expiry if omitted)
   * @returns {Promise<any>} Promise resolving to options chain data
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * // Get options chain for NIFTY with default (nearest) expiry
   * const niftyOptions = await zerodhaConnector.getOptionsChain('NIFTY');
   * 
   * // Get options chain for BANKNIFTY with specific expiry
   * const bankNiftyOptions = await zerodhaConnector.getOptionsChain('BANKNIFTY', '2023-06-29');
   */
  async getOptionsChain(symbol: string, expiry?: string) {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      console.log('Fetching options chain for:', symbol, 'expiry:', expiry || 'nearest')
      
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/optionschain', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken,
          symbol,
          expiry
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.error || `API error: ${response.status}`)
      }

      const data = await response.json()
      
      // Validate the response data structure
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response format from options chain API')
      }
      
      // Ensure expiryDates is an array
      if (!Array.isArray(data.expiryDates)) {
        console.warn('expiryDates is not an array, setting to empty array')
        data.expiryDates = []
      }
      
      // Ensure optionsChain is an array
      if (!Array.isArray(data.optionsChain)) {
        console.warn('optionsChain is not an array, setting to empty array')
        data.optionsChain = []
      }
      
      console.log('Options chain data received:', {
        expiryDatesCount: data.expiryDates.length,
        optionsChainCount: data.optionsChain.length
      })
      
      return data
    } catch (error) {
      console.error('Error fetching options chain:', error)
      throw error
    }
  }

  /**
   * Get account information from Zerodha
   * Retrieves account details such as account type, permissions, etc.
   * 
   * @returns {Promise<AccountInfo>} Promise resolving to account information
   * @throws {Error} If not connected to Zerodha
   * 
   * @example
   * const accountInfo = await zerodhaConnector.getAccountInfo();
   * console.log('Margin trading enabled:', accountInfo.isMarginEnabled);
   */
  async getAccountInfo(): Promise<AccountInfo> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      // Use our server-side API endpoint
      const response = await fetch('/api/connectors/zerodha/account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          apiKey: this.apiKey,
          apiSecret: this.apiSecret,
          accessToken: this.accessToken
        })
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(errorData.message || 'Failed to fetch account info')
      }

      const profileData = await response.json()
      
      // Transform the profile data to match the expected AccountInfo format
      return {
        vipLevel: 0, // Zerodha doesn't have VIP levels
        isMarginEnabled: profileData.product_type === 'MIS',
        isFutureEnabled: true, // Assuming the user has permissions for futures
        isOptionsEnabled: true, // Assuming the user has permissions for options
        isPortfolioMarginRetailEnabled: false // Zerodha doesn't have this feature
      }
    } catch (error) {
      console.error('Failed to fetch account info:', error)
      return {
        vipLevel: 0,
        isMarginEnabled: false,
        isFutureEnabled: false,
        isOptionsEnabled: false,
        isPortfolioMarginRetailEnabled: false
      }
    }
  }

  /**
   * Cancel an open order on Zerodha
   * Cancels a specific order by its order ID
   * 
   * @param {string} orderId The order ID to cancel
   * @param {string} [symbol] Optional symbol parameter for the order
   * @returns {Promise<boolean>} Promise resolving to true if cancellation was successful
   * @throws {Error} If not connected to Zerodha or cancellation fails
   * 
   * @example
   * // Cancel an order by its ID
   * const success = await zerodhaConnector.cancelOrder('23042100000123');
   * if (success) {
   *   console.log('Order cancelled successfully');
   * }
   */
  async cancelOrder(orderId: string, symbol?: string): Promise<boolean> {
    if (!this.isConnected) {
      throw new Error('Not connected to Zerodha')
    }

    try {
      if (!orderId) {
        throw new Error('Order ID is required to cancel an order')
      }

      // Mock implementation for Zerodha - to be implemented later
      console.log('Mock: Cancelling Zerodha order:', { orderId, symbol })
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      // Mock successful cancellation
      console.log(`Mock: Zerodha order ${orderId} cancelled successfully`)
      return true
    } catch (error) {
      console.error('Failed to cancel Zerodha order:', error)
      return false
    }
  }
}
